import React from "react";
import { useAuthentication } from "@planzone/view-models";
import { useFilesCache } from "@components/cache";
import { DocumentIcon } from "@common/documentIcon";
import styles from "./styles.scss";

const imageMimeTypes = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "image/svg+xml",
  "image/bmp",
];

function Thumbnail({
  documentId,
  documentUri,
  mimeType,
}: {
  documentId: string;
  documentUri: string;
  mimeType: string;
}) {
  const cache = useFilesCache();
  const { token } = useAuthentication().account;
  const [state, setState] = React.useState<
    "loadingCache" | "fetching" | "validating" | "success" | "failure"
  >("loadingCache");
  const [uri, setUri] = React.useState("");

  const isImage = imageMimeTypes.includes(mimeType);

  React.useEffect(() => {
    if (!isImage || !documentUri) {
      setState("failure");
      return;
    }

    if (state !== "loadingCache") return;

    cache
      .get(documentId)
      .then((blob) => {
        setUri(URL.createObjectURL(blob));
        setState("validating");
      })
      .catch(() => setState("fetching"));
  }, [documentId, state]);

  React.useEffect(() => {
    if (state !== "fetching") return;

    const controller = new AbortController();

    fetch(documentUri, {
      method: "get",
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
    })
      .then((res) => res.blob())
      .then((blob) => {
        const objectUrl = URL.createObjectURL(blob);
        setUri(objectUrl);
        try {
          cache.add(documentId, blob);
        } catch (e) {}
        setState("validating");
      })
      .catch(() => setState("failure"));

    return () => controller.abort();
  }, [documentUri, state]);

  React.useEffect(() => {
    if (state !== "validating") return;

    const img = new window.Image();
    img.src = uri;
    img.onload = () => {
      setState("success");
      img.remove();
    };
    img.onerror = () => {
      setState("failure");
      img.remove();
    };
  }, [state, uri]);

  if (state === "success") {
    return <img className={styles.thumbnail} src={uri} alt="" />;
  }

  return <DocumentIcon mimeType={mimeType} className={styles.icon} />;
}

export { Thumbnail };
